import type { PieceSpec } from '../game/types';
import { buildPiecePath2D, EAR_MAX } from './piecePath';
import { createCanvas, get2D } from './canvasUtil';

/** 已缩放到"完整拼图"尺寸的源图（供裁剪采样）。 */
export interface PieceSourceImage {
  canvas: HTMLCanvasElement | OffscreenCanvas;
  totalWidth: number;
  totalHeight: number;
}

/** 一块的离屏缓存：预渲染好图像 + 阴影 + 描边，拖拽时直接 drawImage，保证 60fps。 */
export interface CachedPiece {
  piece: PieceSpec;
  canvas: HTMLCanvasElement;
  /** 碎片主体左上角在缓存画布中的偏移（耳朵外凸预留了 padding）。 */
  ox: number;
  oy: number;
  /** 缓存画布尺寸。 */
  width: number;
  height: number;
}

/**
 * 把一块从源图裁剪并预渲染到离屏画布。
 * @param source   源图（完整拼图尺寸）
 * @param piece    块规格
 * @param pieceW   块主体宽
 * @param pieceH   块主体高
 */
export function cachePiece(
  source: PieceSourceImage,
  piece: PieceSpec,
  pieceW: number,
  pieceH: number,
  noise: HTMLCanvasElement | null = null,
): CachedPiece {
  // 耳朵最大外凸量 = EAR_MAX * max(边长)，加少量余量作为 padding。
  const pad = Math.ceil(EAR_MAX * Math.max(pieceW, pieceH)) + 4;
  const cw = Math.ceil(pieceW + pad * 2);
  const ch = Math.ceil(pieceH + pad * 2);
  const canvas = createCanvas(cw, ch);
  const ctx = get2D(canvas);

  // 之后所有绘制以"主体左上角 = (pad, pad)"为原点。
  ctx.translate(pad, pad);

  const path = buildPiecePath2D(piece.edges, pieceW, pieceH);

  // 1) 投影（轻微落地阴影，避免拼合后接缝过重）
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.3)';
  ctx.shadowBlur = 8;
  ctx.shadowOffsetY = 3;
  ctx.fillStyle = '#000';
  ctx.fill(path);
  ctx.restore();

  // 2) 裁剪 + 图像 + 纸张噪点 + 顶面受光 + 细斜面 + 收窄内暗角
  ctx.save();
  ctx.clip(path);
  ctx.drawImage(source.canvas, -piece.baseX, -piece.baseY);
  if (noise) {
    ctx.globalCompositeOperation = 'overlay';
    ctx.globalAlpha = 0.75;
    ctx.drawImage(noise, 0, 0, pieceW, pieceH);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  }
  const lit = ctx.createLinearGradient(0, 0, 0, pieceH);
  lit.addColorStop(0, 'rgba(255,255,255,0.12)');
  lit.addColorStop(0.5, 'rgba(255,255,255,0)');
  lit.addColorStop(1, 'rgba(0,0,0,0.1)');
  ctx.fillStyle = lit;
  ctx.fillRect(-pieceW, -pieceH, pieceW * 3, pieceH * 3);
  const bevel = ctx.createLinearGradient(0, 0, pieceW, pieceH);
  bevel.addColorStop(0, 'rgba(255,255,255,0.25)');
  bevel.addColorStop(0.5, 'rgba(255,255,255,0)');
  bevel.addColorStop(1, 'rgba(0,0,0,0.18)');
  ctx.lineWidth = 2;
  ctx.strokeStyle = bevel;
  ctx.stroke(path);
  ctx.lineWidth = 4;
  ctx.strokeStyle = 'rgba(0,0,0,0.14)';
  ctx.stroke(path);
  ctx.restore();

  // 3) 外描边（细而淡）
  ctx.lineWidth = 0.9;
  ctx.strokeStyle = 'rgba(0,0,0,0.3)';
  ctx.stroke(path);

  return { piece, canvas, ox: pad, oy: pad, width: cw, height: ch };
}
